import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Check, X, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useProductPlans, type ProductPlan } from '../../hooks/useProductPlans';
import { productPlansService } from '../../services/product-plans.service';

interface Props {
  productId: number;
}

export function PlansTab({ productId }: Props) {
  const { data: plans = [], isLoading } = useProductPlans(productId);
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ slug: '', name: '' });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ name: '', order: 0 });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['product-plans', productId] });

  const createMutation = useMutation({
    mutationFn: () => productPlansService.create(productId, { ...form, order: plans.length }),
    onSuccess: () => {
      invalidate();
      setShowCreate(false);
      setForm({ slug: '', name: '' });
      toast.success('Plano criado');
    },
    onError: (e: any) => toast.error(e?.response?.data?.error ?? 'Erro ao criar plano'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ planId, data }: { planId: number; data: { name: string; order: number } }) =>
      productPlansService.update(productId, planId, data),
    onSuccess: () => {
      invalidate();
      setEditingId(null);
      toast.success('Plano atualizado');
    },
    onError: () => toast.error('Erro ao atualizar plano'),
  });

  const deleteMutation = useMutation({
    mutationFn: (planId: number) => productPlansService.delete(productId, planId),
    onSuccess: () => {
      invalidate();
      toast.success('Plano removido');
    },
    onError: (e: any) => toast.error(e?.response?.data?.error ?? 'Erro ao remover plano'),
  });

  if (isLoading) {
    return <p className="text-ink-tertiary">Carregando...</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-ink-primary">Planos do produto</h3>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-ink-primary text-canvas rounded-lg hover:bg-ink-secondary"
        >
          <Plus className="w-4 h-4" /> Novo Plano
        </button>
      </div>

      {plans.length === 0 && (
        <p className="text-sm text-ink-tertiary py-4 text-center bg-canvas rounded-lg border border-border">
          Nenhum plano cadastrado.
        </p>
      )}

      <div className="space-y-2">
        {plans.map((plan: ProductPlan) => (
          <div
            key={plan.id}
            className="flex items-center gap-3 p-3 bg-canvas rounded-lg border border-border"
          >
            <span className="text-xs text-ink-tertiary w-6">{plan.order}</span>
            {editingId === plan.id ? (
              <>
                <input
                  className="input flex-1"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
                <input
                  className="input w-20"
                  type="number"
                  value={editForm.order}
                  onChange={(e) => setEditForm({ ...editForm, order: Number(e.target.value) })}
                />
                <button
                  onClick={() => updateMutation.mutate({ planId: plan.id, data: editForm })}
                  className="p-1.5 rounded-lg bg-subtle text-accent"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setEditingId(null)}
                  className="p-1.5 rounded-lg bg-subtle text-ink-tertiary"
                >
                  <X className="w-4 h-4" />
                </button>
              </>
            ) : (
              <>
                <div className="flex-1">
                  <p className="font-medium text-ink-primary">{plan.name}</p>
                  <p className="text-xs text-ink-tertiary">{plan.slug}</p>
                </div>
                <button
                  onClick={() => {
                    setEditingId(plan.id);
                    setEditForm({ name: plan.name, order: plan.order });
                  }}
                  className="p-1.5 rounded-lg hover:bg-subtle text-ink-tertiary"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    if (confirm(`Remover plano "${plan.name}"?`)) {
                      deleteMutation.mutate(plan.id);
                    }
                  }}
                  className="p-1.5 rounded-lg hover:bg-subtle text-ink-tertiary"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        ))}
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface rounded-xl p-6 w-full max-w-md shadow-xl space-y-4">
            <h3 className="font-semibold">Novo Plano</h3>
            <div>
              <label className="label">Nome</label>
              <input
                className="input w-full"
                value={form.name}
                onChange={(e) =>
                  setForm({
                    name: e.target.value,
                    slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, '-'),
                  })
                }
                placeholder="Enterprise"
              />
            </div>
            <div>
              <label className="label">Slug</label>
              <input
                className="input w-full"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="enterprise"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 text-sm text-ink-secondary"
              >
                Cancelar
              </button>
              <button
                onClick={() => createMutation.mutate()}
                disabled={!form.name || !form.slug || createMutation.isPending}
                className="px-4 py-2 text-sm bg-ink-primary text-canvas rounded-lg disabled:opacity-50"
              >
                Criar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
